import { fmtDate } from "@/lib/format";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Money } from "@/components/Money";
import { TileBadge } from "@/components/filters/TileBadge";
import { cn } from "@/lib/utils";

const CARDS = [
  { key: "revenue", title: "Total Revenue", hint: "Net sales (returns netted)", accent: "bg-teal-600" },
  { key: "gross_profit", title: "Gross Profit", hint: "From sales report GP Value", profit: true, margin: "gross_margin_pct", marginLabel: "GP margin" },
  { key: "total_expenses", title: "Total Expenses", hint: "All expense records, incl. cost of sales", accent: "bg-slate-400", inverse: true },
  { key: "net_profit", title: "Net Profit", hint: "GP Value − Total expenses", profit: true, margin: "net_margin_pct", marginLabel: "Net margin" },
  { key: "cogs", title: "Cost of goods sold", hint: "From sales report Cost", accent: "bg-slate-400", inverse: true },
];

const Margin = ({ value, label, testid }) => (
  <div className="mt-1 text-[11px] font-medium text-slate-600" data-testid={testid}>{label}: {value == null ? "—" : `${value.toFixed(2)}%`}</div>
);

const Delta = ({ cur, prev, inverse }) => {
  if (!prev) return null;
  const pct = ((cur - prev) / Math.abs(prev)) * 100;
  const up = pct >= 0;
  const good = inverse ? !up : up;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={cn("inline-flex items-center gap-0.5 text-[11px] font-medium", good ? "text-green-600" : "text-red-600")}>
      <Icon className="h-3 w-3" /> {Math.abs(pct).toFixed(1)}% vs last month
    </span>
  );
};

const Tile = ({ title, accent, i, testid, children }) => (
  <div className="rise-in relative overflow-hidden rounded-lg border border-slate-200 bg-white p-5 shadow-sm" style={{ animationDelay: `${i * 60}ms` }} data-testid={testid}>
    <span className={cn("absolute left-0 top-0 h-full w-1", accent)} />
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</div>
    {children}
  </div>
);

export const KpiCards = ({ data }) => {
  const prev = data?.single_month ? data?.trend?.[data.trend.length - 2] : null;
  const os = data?.outstanding;
  return (
    <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2 sm:gap-4 md:grid-cols-[repeat(auto-fill,minmax(210px,1fr))]" data-testid="kpi-cards">
      {CARDS.map((c, i) => {
        const v = data?.current?.[c.key] ?? 0;
        return (
          <Tile key={c.key} title={c.title} i={i} accent={c.profit ? (v < 0 ? "bg-red-500" : "bg-green-500") : c.accent} testid={`kpi-card-${c.key}`}>
            {data ? <Money value={v} profit={c.profit} className="mt-3 block text-xl font-semibold text-slate-900 2xl:text-[1.35rem]" data-testid={`kpi-value-${c.key}`} /> : <Skeleton className="mt-3 h-7 w-3/4" />}
            {data && c.margin && <Margin value={data.current[c.margin]} label={c.marginLabel} testid={`kpi-margin-${c.key}`} />}
            <div className="mt-2 flex flex-wrap items-center justify-between gap-1">
              <span className="text-[11px] text-slate-400">{c.hint}</span>
              {data && <Delta cur={v} prev={prev?.[c.key]} inverse={c.inverse} />}
            </div>
            {data && <div className="mt-2"><TileBadge status={data.current.status?.[c.key]} testid={`kpi-badge-${c.key}`} /></div>}
          </Tile>
        );
      })}
      <Tile title="Outstanding" i={5} accent="bg-amber-500" testid="kpi-card-outstanding">
        {data ? <Money value={os?.value ?? 0} className="mt-3 block text-xl font-semibold text-slate-900 2xl:text-[1.35rem]" data-testid="kpi-value-outstanding" /> : <Skeleton className="mt-3 h-7 w-3/4" />}
        <div className="mt-2 text-[11px] text-slate-400">{os?.as_of ? `Snapshot as of ${fmtDate(os.as_of)} · ${os.customers} customers` : "Latest snapshot on or before the range end"}</div>
        {data && <div className="mt-2"><TileBadge status={os?.status} testid="kpi-badge-outstanding" /></div>}
      </Tile>
    </div>
  );
};
